import { Suspense, lazy, useCallback, useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from 'react-query';
import { useParams } from 'react-router-dom';
import { Row } from 'react-table';
import styled from 'styled-components';

import ModalDefault from 'app/components/Modal/ModalDefault';
import TableComponent from 'app/components/Table/TableComponent';
import { QUERIES } from 'app/components/Table/core/constants';
import { stringifyRequestQuery } from 'app/components/Table/core/helpers';
import { ID, TYPE_MODAL } from 'app/components/Table/core/types';
import { LoadingCard } from 'app/components/Table/loading/LoadingCard';
import Typography from 'app/components/Typography';
import RootContentPage from 'app/components/layout/RootContentPage';

import { StockLevelPercentage } from 'app/modules/inventory/items/ItemDetailPage';
import { StockTypeEnum } from 'app/modules/inventory/items/core/enum';
import { Item } from 'app/modules/inventory/items/core/type';
import { useChangeParams, useSyncQueryToState } from 'app/modules/query-state/hooks';
import { useQueryParamStore } from 'app/modules/query-state/store';
import { getSetting } from 'app/modules/system-settings/configuration/core/request';
import {
  SettingType,
  StockLevelSettingType,
} from 'app/modules/system-settings/configuration/core/type';
import {
  downloadTrasaction,
  downloadTrasactionLedger,
} from 'app/modules/system-settings/devices/core/requests';
import { getWards } from 'app/modules/system-settings/locations/wards/core/requests';
import { Ward } from 'app/modules/system-settings/locations/wards/core/type';

import { getResponseError } from 'app/helpers/errors';
import { useNavigateWithSendPath } from 'app/helpers/utils';
import { useBoolean, useNumber } from 'app/hooks';
import { cloneDeep } from 'lodash';

import { PATH } from 'app/constants/path';

import { ReactComponent as IconEdit } from 'app/assets/icons/edit-primary-icon.svg';
import { ReactComponent as IconGrid } from 'app/assets/icons/icon-view-grid-rack.svg';
import { ReactComponent as IconList } from 'app/assets/icons/icon-view-list-rack.svg';

import ButtonSwitchView, { ViewMode } from '../../../components/Button/ButtonSwitchView';
import ViewListBin from '../bins/ViewListBin';
import ViewListBinDrag from '../bins/ViewListBinDrag';
import { getBinInRack } from '../bins/core/requests';
import { Bin } from '../bins/core/type';
import AssignItemModal from '../bins/modal/AssignItem.modal';
import AssignSensorModal from '../bins/modal/AssignSensor.modal';
import { RemoveItemModal } from '../bins/modal/RemoveItem.modal';
import { RemoveSensorModal } from '../bins/modal/RemoveSensor.modaL';
import { UnableToRemoveItemModal } from '../bins/modal/UnableToRemoveItem.modal';
import InformationRackCard from './components/InformationRackCard';
import LabelSyncDataRack from './components/LabelSyncDataRack';
import { binInRackColumns } from './core/columns';
import { ActionBin } from './core/enum';
import { getRack, synchronizedRack } from './core/requests';
import { useRackSocket } from './core/socket';
import { Rack } from './core/type';
import { getListTabletName, mergeQtyItems, showUpdateAtRack } from './core/utils';
import UpdateRackModal from './modal/FormRack.modal';
import { QuantityUpdateModal } from './modal/QuantityUpdateModal';
import { ShortForModal } from './modal/ShortFor.modal';
import { changeGroupByItemsSelector, useRackStore } from './store';
import { SuspensedView } from 'app/routing/PrivateRoutes';
import { useOrganizationStore } from 'theme/layout/components/aside/store';

const UpdateBaseWeightModal = lazy(
  () => import('app/modules/inventory/racks/modal/UpdateBaseWeightModal'),
);

const RemoveExpiriedSensorModal = lazy(
  () => import('app/modules/inventory/bins/modal/RemoveExpiriedSensor.modal'),
);
const AssignExpiredSensorModal = lazy(
  () => import('app/modules/inventory/bins/modal/AssignExpiredSensor.modal'),
);

const CHANGE_VIEW_MESSAGES = ['Switch to Grid view', 'Switch to Table view'];

const TableWrapper = styled.div`
  .custom-input-search {
    width: 400px;
  }
`;

const WrapperEditLayout = styled.div`
  display: flex;
  align-items: center;
  padding: 0px 20px;
  cursor: pointer;
  gap: 8px;

  &:hover {
    opacity: 0.6;
    svg {
      opacity: 0.6;
    }
  }
`;

const RackDetailManagement = () => {
  useSyncQueryToState(
    window.location.pathname,
    {
      mode: ViewMode.list,
      limit: 50,
      page: 1,
      orderBy: 'name',
      orderDirection: 'ASC',
    },
    { skipFields: ['mode'] },
  );

  const mode = useQueryParamStore((s) => s.state.curParams.mode);
  const { onChangeParams } = useChangeParams();
  const organization = useOrganizationStore((state) => state.state.organization);
  const params = useParams<{ rackId: string; orgId: string }>();
  const orgId = params.orgId ? Number(params.orgId) : organization?.id;
  const customNavigate = useNavigateWithSendPath();
  const setRackState = useRackStore((s) => s.setState);
  const { rack, inSession, tablets, bins, changeItems, savedItems } = useRackStore((s) => s.state);
  const changeGroupByItems = useRackStore(changeGroupByItemsSelector);
  const [wards, setWards] = useState<Ward[]>([]);
  const isEditRack = useBoolean(false);
  const [selectBin, setSelectBin] = useState<Bin | null>(null);
  const isAssignItemInBin = useBoolean(false);
  const isAssignSensorInBin = useBoolean(false);
  const isAssignSensorExpiredInBin = useBoolean(false);
  const isRemoveSensorExpiredInBin = useBoolean(false);
  const isRemoveItemInBin = useBoolean(false);
  const isRemoveSensorInBin = useBoolean(false);
  const unableToRemoveItem = useBoolean(false);
  const showShortForModal = useBoolean(false);
  const showQuantityUpdateModal = useBoolean(false);
  const showUpdateBaseWeightModal = useBoolean(false);
  const isLoadingPage = useBoolean(false);
  const totalBin = useNumber(0);
  const refetchTimestamp = useNumber(0);
  const isEditLayoutRack = useBoolean(false);
  const isGridView = String(mode) === String(ViewMode.nonList);
  const [stockPercentages, setStockPercentages] = useState<StockLevelPercentage>({
    restockLevel: 0,
    alarmLevel: 0,
  });

  const sensor = {
    baseWeight: selectBin?.sensor?.baseWeight ?? 0,
    id: selectBin?.sensor?.id,
    code: selectBin?.sensor?.code ?? '',
  };

  const onClearSession = (gridView: boolean, immediateUpdate: boolean, callback: () => void) => {
    if (immediateUpdate) {
      useRackStore.getState().saveCompletedTransaction();
    } else {
      if (gridView) {
        getBinInRackViewGrid(false, callback);
      } else {
        refetchTimestamp.setValue(+new Date());
        callback();
      }
    }
  };

  const getDataRack = async (oldRackId: number, isGetWards = false, showLoading = true) => {
    if (showLoading) {
      isLoadingPage.setValue(true);
    }

    const dataRes = await getRack(oldRackId, orgId);
    if (dataRes) {
      useRackStore.getState().setState({ rack: dataRes });
    }
    if (isGetWards) {
      const dataResWards = await getWards(
        stringifyRequestQuery({
          page: 1,
          limit: 100,
        }),
      );
      const newResWards = dataResWards.data?.items || [];
      newResWards.unshift({
        createdAt: '',
        id: 0,
        name: 'None',
        updatedAt: '',
      });
      setWards(newResWards);
    }

    if (showLoading) {
      isLoadingPage.setValue(false);
    }
  };

  const getDataItemResponse = useCallback(async (newData: Item, total: number) => {
    const dataRes = (await getSetting(SettingType.stockLevel)) || [];
    const itemType =
      Number(newData?.stockType) === StockTypeEnum.Chargeable ? 'chargeable' : 'nonChargeable';

    const restockValueFromItem = dataRes.find((el) => el.name === StockLevelSettingType.restock);
    const alarmValueFromItem = dataRes.find((el) => el.name === StockLevelSettingType.alarm);

    setStockPercentages({
      restockLevel: restockValueFromItem ? restockValueFromItem.value[itemType] : 0,
      alarmLevel: alarmValueFromItem ? alarmValueFromItem.value[itemType] : 0,
    });
  }, []);

  useRackSocket(isGridView, onClearSession);

  useEffect(() => {
    if (!params?.rackId) {
      customNavigate(PATH.RACKS);
      return;
    }

    getDataRack(Number(params.rackId), true);
  }, [params.rackId]);

  useEffect(() => {
    if (!isGridView) return;
    getBinInRackViewGrid();
  }, [isGridView]);

  useEffect(() => {
    return () => {
      useRackStore.getState().clearState();
    };
  }, []);

  const onClickCloseModalUpdate = async (newRack?: Rack) => {
    isEditRack.setValue(false);

    if (newRack) {
      if (isGridView) fetchBins();
      setRackState({ rack: newRack });
    }
  };
  const onClickModal = (key: ActionBin, originBin: Bin) => {
    setSelectBin(originBin);
    switch (key) {
      case ActionBin.assignItem:
        isAssignItemInBin.setValue(true);
        break;
      case ActionBin.removeItem:
        isRemoveItemInBin.setValue(true);
        break;
      case ActionBin.assignSensor:
        isAssignSensorInBin.setValue(true);
        break;
      case ActionBin.removeSensor:
        isRemoveSensorInBin.setValue(true);
        break;
      case ActionBin.assignSensorExpired:
        isAssignSensorExpiredInBin.setValue(true);
        break;
      case ActionBin.removeSensorExpired:
        isRemoveSensorExpiredInBin.setValue(true);
        break;
      case ActionBin.shortFor:
        showShortForModal.setValue(true);
        break;
      case ActionBin.updateQuantity:
        showQuantityUpdateModal.setValue(true);
        break;
      case ActionBin.updateBaseWeight:
        showUpdateBaseWeightModal.setValue(true);
        break;
      default:
        break;
    }
  };
  const onClickCloseModalAssignItemInBin = async (updateBin?: Bin) => {
    isAssignItemInBin.setValue(false);
    if (updateBin) {
      await getBinInRackViewGrid();
      getDataRack(Number(rack.id));
    }
  };
  const onClickCloseModalAssignSensorInBin = async (updateBin?: Bin) => {
    isAssignSensorInBin.setValue(false);
    if (updateBin) {
      await getBinInRackViewGrid();
      getDataRack(Number(rack.id));
    }
  };

  const onSubmitSyncRack = async () => {
    isLoadingPage.setValue(true);
    try {
      await synchronizedRack(rack.id, {
        isSync: true,
      });
      getDataRack(Number(rack.id));
    } finally {
      isLoadingPage.setValue(false);
    }
  };

  const onNavigate = (itemId: string) => {
    customNavigate(PATH.ITEMS_DETAIL.replace(':id', String(itemId)));
  };

  const fetchBins = async () => {
    const dataRes = await getBinInRack(`page=1&limit=50&rack=${params.rackId}`);
    if (dataRes?.data?.items) {
      setRackState({ bins: dataRes?.data?.items });
    }
    if (dataRes?.data?.total) {
      totalBin.setValue(dataRes?.data?.total);
    }
  };

  const onChangeViewRack = (newView: ViewMode) => async () => {
    onChangeParams({ mode: newView });
    // if (newView == ViewMode.nonList) {
    //   fetchBins();
    // }
  };

  const getBinInRackViewGrid = async (load = true, callback?: () => void) => {
    load && isLoadingPage.setValue(true);
    await fetchBins();
    callback?.();
    load && isLoadingPage.setValue(false);
  };

  const { mutate: mutateDownloadBin, isLoading: isDownloadingBin } = useMutation({
    mutationKey: ['download-bin'],
    mutationFn: (binParams: { binId: ID }) => downloadTrasaction(binParams),
  });

  const { mutate: mutateDownloadBinLedger, isLoading: isDownloadingBinLedger } = useMutation({
    mutationKey: ['download-bin-ledger'],
    mutationFn: (binParams: { binId: ID }) => downloadTrasactionLedger(binParams),
  });

  const handleDownloadBin = (binId: ID) => () => mutateDownloadBin({ binId });

  const handleDownloadBinLedger = (binId: ID) => () => mutateDownloadBinLedger({ binId });

  const handleEditLayoutRack = () => {
    if (isGridView) {
      isEditLayoutRack.setValue(true);
      return;
    }

    isLoadingPage.setValue(true);
    fetchBins().then(() => {
      isLoadingPage.setValue(false);
      isEditLayoutRack.setValue(true);
    });
  };

  const handleCloseRemoveExpiriedSensorModal = async (updateBin?: boolean) => {
    isRemoveSensorExpiredInBin.setValue(false);
    if (updateBin) {
      await getBinInRackViewGrid();
      getDataRack(Number(rack.id));
    }
  };

  const handleCloseAssignExpiredSensorModal = async (updateBin?: boolean) => {
    isAssignSensorExpiredInBin.setValue(false);
    if (updateBin) {
      await getBinInRackViewGrid();
      getDataRack(Number(rack.id));
    }
  };

  const handleCloseUpdateBaseWeightModal = async () => {
    showUpdateBaseWeightModal.setValue(false);
    fetchBins();
  };

  const renderModalForm = (originalData: Bin, typeModal?: TYPE_MODAL) => {
    const selectedEntity = {
      baseWeight: originalData?.sensor?.baseWeight ?? 0,
      id: originalData?.sensor?.id,
      code: originalData?.sensor?.code ?? '',
    };

    switch (typeModal) {
      case TYPE_MODAL.REMOVE_ITEM:
        return (
          <RemoveItemModal
            bin={originalData}
            itemId={originalData.item?.id}
            onUpdateRack={() => {
              getDataRack(Number(rack.id));
            }}
            showUnableToRemoveModal={() => unableToRemoveItem.setValue(true)}
          />
        );
      case TYPE_MODAL.REMOVE_SENSOR:
        return (
          <RemoveSensorModal
            bin={originalData}
            sensorId={originalData.sensor?.id}
            onUpdateRack={() => {
              getDataRack(Number(rack.id));
            }}
            showUnableToRemoveModal={() => unableToRemoveItem.setValue(true)}
          />
        );
      case TYPE_MODAL.ASSIGN_SENSOR:
        return (
          <AssignSensorModal
            selectBin={originalData}
            showUnableToAssignModal={() => unableToRemoveItem.setValue(true)}
          />
        );
      case TYPE_MODAL.ASSIGN_ITEM:
        return (
          <AssignItemModal
            selectBin={originalData}
            onUpdateRack={() => {
              getDataRack(Number(rack.id));
            }}
          />
        );
      case TYPE_MODAL.ASSIGN_SENSOR_EXPIRED:
        return (
          <Suspense fallback={<LoadingCard />}>
            <AssignExpiredSensorModal
              selectBin={originalData}
              onUpdateRack={() => {
                getDataRack(Number(rack.id));
              }}
            />
          </Suspense>
        );
      case TYPE_MODAL.REMOVE_SENSOR_EXPIRED:
        return (
          <Suspense fallback={<LoadingCard />}>
            <RemoveExpiriedSensorModal
              selectBin={originalData}
              onUpdateRack={() => {
                getDataRack(Number(rack.id));
              }}
              sensorId={originalData.expirySensor?.id}
            />
          </Suspense>
        );
      case TYPE_MODAL.SHORT_FOR:
        return (
          <ShortForModal
            selectBin={originalData}
            wardId={rack.location?.id}
            wardName={rack.location?.name || ''}
          />
        );
      case TYPE_MODAL.EDIT_QUANTITY:
        return (
          <QuantityUpdateModal
            selectBin={originalData}
            wardId={rack.location?.id}
            wardName={rack.location?.name || ''}
          />
        );
      case TYPE_MODAL.UPDATE_BASE_WEIGHT:
        return (
          <SuspensedView>
            <UpdateBaseWeightModal selectedEntity={selectedEntity} />
          </SuspensedView>
        );
      default:
        return <div />;
    }
  };
  const onCloseModalRemoveItem = (updateBin?: boolean) => {
    if (updateBin) {
      getBinInRackViewGrid();
      getDataRack(Number(rack.id));
    }
    isRemoveItemInBin.setValue(false);
  };

  const onCloseModalRemoveSensor = (updateBin?: boolean) => {
    if (updateBin) {
      getBinInRackViewGrid();
      getDataRack(Number(rack.id));
    }
    isRemoveSensorInBin.setValue(false);
  };

  if (!rack.id || isLoadingPage.value) {
    return (
      <div className="w-100 h-100 d-flex justify-content-center align-items-center">
        <LoadingCard />
      </div>
    );
  }

  const mappingWithSocketDataMiddleware = (data: any) => {
    if (!data) {
      return undefined;
    }

    const rows = cloneDeep(data);
    // Mapping qty and stock level
    rows.data.items = rows.data.items.map(
      mergeQtyItems(changeItems, savedItems, changeGroupByItems),
    );
    if (changeItems.length > 0 && changeItems.some((el) => el.episode_id === 0)) {
      console.log('mergeQtyItems Rack', changeItems);
      useRackStore.getState().saveCompletedTransaction();
    }

    return rows;
  };

  const renderViewRack = () => {
    if (isEditLayoutRack.value) {
      return (
        <ViewListBinDrag
          isRackCustom={rack.isCustom}
          totalColumns={rack.col ?? 0}
          totalRows={rack.row ?? 0}
          totalBins={totalBin.value}
          bins={bins}
          onClickModal={onClickModal}
          inSession={inSession}
          rackId={rack.id}
          fetchBins={fetchBins}
          onBack={() => isEditLayoutRack.setValue(false)}
        />
      );
    }

    if (isGridView) {
      const newBins = cloneDeep(bins).map(
        mergeQtyItems(changeItems, savedItems, changeGroupByItems),
      );
      if (changeItems.length > 0 && changeItems.some((el) => el.episode_id === 0)) {
        useRackStore.getState().saveCompletedTransaction();
      }
      return <ViewListBin bins={newBins} onClickModal={onClickModal} inSession={inSession} />;
    }

    return (
      <TableWrapper>
        <TableComponent
          getList={getBinInRack as any}
          nameQuery={QUERIES.BIN_IN_RACK_LIST}
          originColumns={binInRackColumns(
            inSession,
            onNavigate,
            rack.location?.id,
            handleDownloadBin,
            handleDownloadBinLedger,
          )}
          titleModal="bin"
          placeholderSearch="Search with bin, item, mac address and SKU"
          isBorder={false}
          isRowClickable={false}
          onClickBodyTable={(_row: Row) => () => {
            //do nothing
          }}
          ModalOutsideTable={renderModalForm}
          moreQuery={`&rack=${params.rackId || ''}`}
          isSortAndSearchFrontEnd
          getTotal={totalBin.setValue}
          refetchTimestamp={refetchTimestamp.value}
          mappingDataMiddleware={mappingWithSocketDataMiddleware}
          syncParamUrl
          customSearchFields={[
            'name',
            'item.name',
            'item.skuId',
            'sensor.code',
            'expirySensor.name',
          ]}
          getDataItemResponse={getDataItemResponse}
        />
      </TableWrapper>
    );
  };

  return (
    <>
      <RootContentPage
        title=""
        header={
          <div className="d-flex flex-column">
            <InformationRackCard
              rackItem={rack}
              onChangeEditRack={() => isEditRack.setValue(true)}
              totalBin={totalBin.value}
              onSyncRack={onSubmitSyncRack}
              isRedirect
              inSession={inSession}
              activeTabletName={tablets.length ? getListTabletName(tablets) : undefined}
            />
            <div
              className="d-flex align-items-center justify-content-between"
              style={{ marginTop: 32 }}
            >
              <Typography variant="body-3" color="neutral-7" style={{ marginRight: 12 }}>
                {rack.updatedAt ? `Last update: ${showUpdateAtRack(rack.updatedAt)}` : ''}
              </Typography>
              <LabelSyncDataRack isSync={rack.isSync} />
            </div>
          </div>
        }
      >
        <div className="position-relative">
          {renderViewRack()}
          <div className="position-absolute" style={{ top: 28, right: 28 }}>
            <div className="d-flex align-items-center">
              {isEditLayoutRack.value ? null : (
                <>
                  {rack.isCustom && (
                    <WrapperEditLayout onClick={handleEditLayoutRack}>
                      <IconEdit />
                      <Typography variant="body-3" color="#0080F0" fontWeight={600}>
                        Rack management
                      </Typography>
                    </WrapperEditLayout>
                  )}
                  <ButtonSwitchView
                    view={Number(mode) as ViewMode}
                    onClickButton={onChangeViewRack}
                    messages={CHANGE_VIEW_MESSAGES}
                    iconList={[IconList, IconGrid]}
                  />
                </>
              )}
            </div>
          </div>

          {isEditRack.value && (
            <ModalDefault
              title="Edit rack"
              onClickCloseModal={() => isEditRack.setValue(false)}
              styleBodyModalDialog={{ overflowY: 'unset' }}
            >
              <UpdateRackModal
                itemRack={rack}
                wards={wards}
                onClickCloseModal={onClickCloseModalUpdate}
                isEditLayoutRack={isEditLayoutRack.value}
                setRefetchTimestamp={refetchTimestamp.setValue}
              />
            </ModalDefault>
          )}
          {isAssignItemInBin.value && (
            <AssignItemModal
              selectBin={selectBin}
              onClickCloseModal={onClickCloseModalAssignItemInBin}
            />
          )}
          {isAssignSensorInBin.value && (
            <AssignSensorModal
              selectBin={selectBin}
              onClickCloseModal={onClickCloseModalAssignSensorInBin}
              showUnableToAssignModal={() => unableToRemoveItem.setValue(true)}
            />
          )}
          {isRemoveItemInBin.value && (
            <RemoveItemModal
              onCloseModal={onCloseModalRemoveItem}
              bin={selectBin}
              itemId={selectBin?.item?.id}
              showUnableToRemoveModal={() => unableToRemoveItem.setValue(true)}
            />
          )}
          {isRemoveSensorInBin.value && (
            <RemoveSensorModal
              onCloseModal={onCloseModalRemoveSensor}
              bin={selectBin}
              sensorId={selectBin?.sensor?.id}
              showUnableToRemoveModal={() => unableToRemoveItem.setValue(true)}
            />
          )}
          {isAssignSensorExpiredInBin.value && (
            <Suspense fallback={<LoadingCard />}>
              <AssignExpiredSensorModal
                selectBin={selectBin}
                isGridView={isGridView}
                onClickCloseModal={handleCloseAssignExpiredSensorModal}
                isAssignSensorExpiredInBin={isAssignSensorExpiredInBin}
              />
            </Suspense>
          )}
          {isRemoveSensorExpiredInBin.value && (
            <Suspense fallback={<LoadingCard />}>
              <RemoveExpiriedSensorModal
                selectBin={selectBin}
                isGridView={isGridView}
                sensorId={selectBin?.expirySensor?.id}
                isRemoveSensorExpiredInBin={isRemoveSensorExpiredInBin}
                onClickCloseModal={handleCloseRemoveExpiriedSensorModal}
              />
            </Suspense>
          )}
          {unableToRemoveItem.value && (
            <UnableToRemoveItemModal
              onClose={() => unableToRemoveItem.setValue(false)}
              show={unableToRemoveItem.value}
            />
          )}
          {showShortForModal.value && (
            <ShortForModal
              selectBin={selectBin}
              wardId={rack.location?.id}
              wardName={rack.location?.name || ''}
              onClickCloseModal={() => showShortForModal.setValue(false)}
            />
          )}
          {showQuantityUpdateModal.value && (
            <QuantityUpdateModal
              selectBin={selectBin}
              wardId={rack.location?.id}
              wardName={rack.location?.name || ''}
              onClickCloseModal={() => {
                showQuantityUpdateModal.setValue(false);
                fetchBins();
              }}
            />
          )}
          <SuspensedView>
            {showUpdateBaseWeightModal.value && (
              <UpdateBaseWeightModal
                selectedEntity={sensor}
                onClickCloseModal={handleCloseUpdateBaseWeightModal}
              />
            )}
          </SuspensedView>
          {isLoadingPage.value && isGridView && <LoadingCard />}
        </div>
      </RootContentPage>

      {(isDownloadingBin || isDownloadingBinLedger) && <LoadingCard />}
    </>
  );
};

export default RackDetailManagement;
